<template>
  <section id="capabilities" class="capabilities-section">
    <div class="container py-5">
      <div class="capabilities-heading" data-aos="fade-up" data-aos-duration="900">
        <h2>{{ data.title }}</h2>
        <p>{{ data.subtitle }}</p>
      </div>

      <div
        ref="capabilitiesGrid"
        class="capabilities-grid"
        @scroll.passive="updateSlide"
        @pointerdown="startDrag"
        @pointermove="drag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @pointerleave="endDrag"
      >
        <article
          v-for="(item, index) in data.items"
          :key="item.title"
          class="capability-card"
          data-aos="fade-up"
          :data-aos-duration="1000 + index * 100"
        >
          <span class="capability-card__icon" :class="item.icon" aria-hidden="true"></span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </article>
      </div>

      <div class="slider-pagination" :aria-label="data.paginationLabel">
        <button
          v-for="(_, index) in data.items"
          :key="index"
          type="button"
          :class="{ active: currentSlide === index }"
          :aria-label="`Show capability slide ${index + 1}`"
          @click="goToSlide(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "portcapabilities",
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentSlide: 0,
      rotationTimer: null,
      isDragging: false,
      dragStartX: 0,
      dragStartScrollLeft: 0,
    };
  },
  mounted() {
    this.rotationTimer = window.setInterval(this.rotateSlide, 4500);
  },
  beforeUnmount() {
    window.clearInterval(this.rotationTimer);
  },
  methods: {
    updateSlide(event) {
      const slides = Array.from(event.target.children);
      const closestSlide = slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - event.target.scrollLeft);
        return distance < closest.distance ? { distance, index } : closest;
      }, { distance: Infinity, index: 0 });
      this.currentSlide = closestSlide.index;
    },
    goToSlide(index) {
      const grid = this.$refs.capabilitiesGrid;
      const slide = grid && grid.children[index];
      if (!slide) return;
      grid.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
      this.currentSlide = index;
    },
    startDrag(event) {
      const grid = this.$refs.capabilitiesGrid;
      if (!grid || grid.scrollWidth <= grid.clientWidth) return;
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = grid.scrollLeft;
      grid.setPointerCapture(event.pointerId);
    },
    drag(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      this.$refs.capabilitiesGrid.scrollLeft = this.dragStartScrollLeft - (event.clientX - this.dragStartX);
    },
    endDrag(event) {
      if (!this.isDragging) return;
      this.isDragging = false;
      const grid = this.$refs.capabilitiesGrid;
      if (grid && event.pointerId !== undefined && grid.hasPointerCapture(event.pointerId)) {
        grid.releasePointerCapture(event.pointerId);
      }
    },
    rotateSlide() {
      if (window.innerWidth > 700 || this.currentSlide >= this.data.items.length - 1) {
        if (this.currentSlide >= this.data.items.length - 1) {
          window.clearInterval(this.rotationTimer);
        }
        return;
      }
      this.goToSlide(this.currentSlide + 1);
    },
  },
};
</script>

<style lang="scss">
@import "../assets/styles/_website.scss";

.capabilities-section {
  background: linear-gradient(180deg, $warm-ivory 0%, #f4efe9 100%);
  color: $dark-blue;
  padding: 1.5rem 0;

  .capabilities-heading {
    max-width: 50rem;
    margin-bottom: 2.25rem;
  }

  h2 {
    margin: 0.7rem 0 1rem;
    color: $dark-blue;
    font-size: clamp(2.2rem, 4vw, 3.5rem);
    font-weight: 800;
    letter-spacing: -0.06em;
    line-height: 0.98;
  }

  .capabilities-heading p {
    max-width: 42rem;
    color: $muted;
    line-height: 1.7;
    margin: 0;
  }

  .capabilities-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 950px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
      display: flex;
      gap: 0.9rem;
      margin-right: -1rem;
      overflow-x: auto;
      padding: 0 1rem 1rem 0;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-x;
      cursor: grab;

      overscroll-behavior-x: contain;

      &:active {
        cursor: grabbing;
      }

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .capability-card {
    min-height: 205px;
    padding: 1.5rem;
    background: white;
    border: 1px solid #e1e9ef;
    border-radius: 1rem;
    box-shadow: 0 12px 26px rgba(10, 37, 65, 0.06);

    @media (max-width: 700px) {
      flex: 0 0 86%;
      min-height: 215px;
      background: $dark-blue;
      border-color: rgba(255, 255, 255, 0.12);
      scroll-snap-align: start;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.7rem;
      height: 2.7rem;
      margin-bottom: 1.2rem;
      border-radius: 0.8rem;
      background: rgba(110, 195, 197, 0.16);
      color: $blue-green;
      font-size: 1.05rem;

      @media (max-width: 700px) {
        background: rgba(110, 195, 197, 0.2);
        color: white;
      }
    }

    h3 {
      margin: 0 0 0.55rem;
      color: $dark-blue;
      font-size: 1.25rem;
      font-weight: 800;
      letter-spacing: -0.03em;
      line-height: 1.1;

      @media (max-width: 700px) {
        color: white;
      }
    }

    p {
      margin: 0;
      color: $muted;
      font-size: 0.93rem;
      line-height: 1.6;

      @media (max-width: 700px) {
        color: rgba(255, 255, 255, 0.82);
      }
    }
  }

  .slider-pagination {
    display: none;
    width: 100%;
    gap: 0.35rem;
    margin-top: 1rem;

    button {
      flex: 1 1 0;
      width: auto;
      height: 3px;
      padding: 0;
      border: 0;
      background: rgba(10, 37, 65, 0.2);
      cursor: pointer;
      transition: width 0.2s ease, background 0.2s ease;

      &.active {
        background: $blue-green;
      }
    }

    @media (max-width: 700px) {
      display: flex;
    }
  }
}
</style>